import { isAfter, isBefore, subHours } from 'date-fns';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useFeatureValue } from '../growthbook';
import { useOnUserLogin } from '../hooks/use-on-user-login';
import { useGetUserQuery } from '../slices';
const FIRST_VISIT_KEY = 'firstVisitTimestamp';
const SECOND_VISIT_KEY = 'secondVisitTimestamp';
const ANONYMOUS_SALE_DISCOUNT = 0.25;
const DISCOUNT_AND_PERCENTAGE = {
  discount: ANONYMOUS_SALE_DISCOUNT,
  discountPercentage: ANONYMOUS_SALE_DISCOUNT * 100
};
type SaleConfig = {
  waitingPeriodHours: number;
  durationHours: number;
};
const DEFAULT_SALE_CONFIG: SaleConfig = {
  waitingPeriodHours: 24,
  durationHours: 24
};
type AnonymousSaleStatus = {
  isActive: false;
  until: null;
} | {
  isActive: true;
  until: Date;
};
type AnonymousSaleResult = AnonymousSaleStatus & {
  discount: number;
  discountPercentage: number;
};
const AnonymousSaleContext = createContext<AnonymousSaleResult | null>(null);

/**
 * Provider that ensures sale data is computed once on initial app load
 * and is kept consistent across all components.
 *
 * Also marks sale as "expired" if user logs in before the sale is activated.
 */
export const AnonymousSaleProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const featureFlagConfig = useFeatureValue?.('anonymous-sale-config', DEFAULT_SALE_CONFIG) ?? DEFAULT_SALE_CONFIG;
  const {
    data: userData
  } = useGetUserQuery();
  const user = userData?.data?.user;
  const saleConfig = isSaleConfigValid(featureFlagConfig) ? featureFlagConfig : DEFAULT_SALE_CONFIG;
  const saleWaitingPeriodMs = saleConfig.waitingPeriodHours * 60 * 60 * 1000;
  const saleDurationMs = saleConfig.durationHours * 60 * 60 * 1000;
  useOnUserLogin(() => {
    if (typeof window === 'undefined') return;

    // Set sentinel value when user signs up/logs in during waiting period
    try {
      const secondVisitTimeString = localStorage.getItem(SECOND_VISIT_KEY);
      if (secondVisitTimeString) return; // Don't overwrite activated or expired timestamps

      const firstVisitTimeString = localStorage.getItem(FIRST_VISIT_KEY);
      if (!firstVisitTimeString) return; // This is the user's first visit

      const firstVisitTime = Number(firstVisitTimeString);
      const timeSinceFirstVisit = Date.now() - firstVisitTime;

      // Set sentinel if they're in the waiting period (< 24hrs) before sale activates
      if (timeSinceFirstVisit < saleWaitingPeriodMs) {
        const sentinel = firstVisitTime - saleDurationMs;
        // Expire sale before it starts
        localStorage.setItem(SECOND_VISIT_KEY, sentinel.toString());
        localStorage.removeItem(FIRST_VISIT_KEY);
      }
    } catch (error) {
      // Silent fail - don't break auth flow over sale feature
      console.error('Failed to set anonymous sale sentinel:', error);
    }
  });
  const saleData = useMemo<AnonymousSaleStatus>(() => {
    // SSR guard
    if (typeof window === 'undefined') {
      return {
        isActive: false,
        until: null
      };
    }

    // Mechanism for turning off feature
    if (saleConfig.durationHours === 0) return {
      isActive: false,
      until: null
    };
    if (user?.dateSignUp) {
      const accountCreationDate = new Date(user.dateSignUp);
      const saleDurationCutoff = subHours(new Date(), saleConfig.durationHours);

      // If account was created before the sale duration cutoff, don't show the sale
      if (isBefore(accountCreationDate, saleDurationCutoff)) {
        return {
          isActive: false,
          until: null
        };
      }
    }
    try {
      const secondVisitTimeString = localStorage.getItem(SECOND_VISIT_KEY);

      // Sale activated
      if (secondVisitTimeString) {
        const secondVisitTime = Number(secondVisitTimeString);
        const saleEndTime = new Date(secondVisitTime + saleDurationMs);

        // Sale expired
        if (isAfter(new Date(), saleEndTime)) {
          return {
            isActive: false,
            until: null
          };
        }

        // Sale still active
        return {
          isActive: true,
          until: saleEndTime
        };
      }
      const firstVisitTimeString = localStorage.getItem(FIRST_VISIT_KEY);

      // First ever visit
      if (!firstVisitTimeString) {
        localStorage.setItem(FIRST_VISIT_KEY, Date.now().toString());
        return {
          isActive: false,
          until: null
        };
      }
      const now = new Date();
      const firstVisitTime = Number(firstVisitTimeString);

      // This visit activates the sale
      if (isAfter(now, new Date(firstVisitTime + saleWaitingPeriodMs))) {
        localStorage.setItem(SECOND_VISIT_KEY, now.getTime().toString());
        localStorage.removeItem(FIRST_VISIT_KEY);
        const saleEndTime = new Date(now.getTime() + saleDurationMs);
        return {
          isActive: true,
          until: saleEndTime
        };
      }

      // Still in waiting period before sale activates
      return {
        isActive: false,
        until: null
      };
    } catch (error) {
      return {
        isActive: false,
        until: null
      };
    }
  }, [saleDurationMs, saleWaitingPeriodMs, user, saleConfig.durationHours]);
  return <AnonymousSaleContext.Provider value={{
    ...saleData,
    ...DISCOUNT_AND_PERCENTAGE
  }} data-sentry-element="unknown" data-sentry-component="AnonymousSaleProvider" data-sentry-source-file="anonymous-sale-context.tsx">
      {children}
    </AnonymousSaleContext.Provider>;
};
function isSaleConfigValid(config: Record<string, unknown>): config is SaleConfig {
  return typeof config.waitingPeriodHours === 'number' && typeof config.durationHours === 'number';
}
export const useAnonymousSale = (): AnonymousSaleResult => {
  const context = useContext(AnonymousSaleContext);
  if (context === null) return {
    isActive: false,
    until: null,
    ...DISCOUNT_AND_PERCENTAGE
  };
  return context;
};